import apiClient from "../lib/apiClient";
import { SESSION_REFRESH_URL } from "../constants/api";
import {
    patchMasterBlockFloorPermissions,
    transformUserPermissionArr,
} from "../components/common/CommonFunctions";
import { SESSION_STORAGE_HYDRATED_EVENT } from "../hooks/use-navigation";
import { enforceOrgAdminBranchPolicy, syncMultiBranchFlag } from "./branchSession";

const SESSION_REFRESH_KEYS = [
    "is_super_admin",
    "is_admin",
    "is_system_admin",
    "user_group_id",
    "employee_id",
    "employee_name",
    "designation",
    "department",
    "profile",
    "loginUserPermission",
    "employeeProfileAccess",
    "modules",
    "subModule",
    "planName",
    "reportingManager",
    "is_branch_admin",
    "can_create",
    "can_edit",
    "can_delete",
    "branch_id",
    "branch_name",
    "branch_avail",
    "multi_branch",
    "org_admin_branch_selection",
    "is_password_updated",
    "is_training_enabled",
];

function writeStorageValue(storage, key, value) {
    if (value === undefined || value === null) return;
    storage.setItem(key, typeof value === "object" ? JSON.stringify(value) : String(value));
}

/** Apply a login/session payload to sessionStorage (and localStorage when remembered). */
export function applySessionPayload(data) {
    if (!data || typeof data !== "object") return false;

    const permissions = data.permissions?.permissions?.length
        ? patchMasterBlockFloorPermissions(
            transformUserPermissionArr([...data.permissions.permissions])
        )
        : [];

    const sessionPatch = {
        is_super_admin: data.is_super_admin,
        is_admin: data.is_admin,
        is_system_admin: data.is_system_admin,
        user_group_id: data.user_group_id,
        employee_id: data.employee_id,
        employee_name: data.employee_name,
        designation: data.designation,
        department: data.department,
        department_id: data.department_id ?? null,
        profile: data.profile,
        loginUserPermission: permissions,
        employeeProfileAccess: data.permissions?.profile_access || {},
        modules: data.modules ? [...data.modules] : [],
        subModule: data.sub_modules ? [...data.sub_modules] : [],
        planName: data.plan_name,
        reportingManager: data.is_reproting_manager,
        is_branch_admin: data.is_branch_admin ?? false,
        can_create: data.can_create ?? false,
        can_edit: data.can_edit ?? false,
        can_delete: data.can_delete ?? false,
        branch_id: data.branch_id ?? null,
        branch_name: data.branch_name ?? "",
        branch_avail: data.branch_avail ?? false,
        multi_branch: data.multi_branch ?? false,
        org_admin_branch_selection: data.org_admin_branch_selection ?? true,
        is_password_updated: data.is_password_updated,
        is_training_enabled: data.is_training_enabled ?? false,
    };

    const previousGroupId = sessionStorage.getItem("user_group_id");
    const previousPermissions = sessionStorage.getItem("loginUserPermission");

    for (const key of SESSION_REFRESH_KEYS) {
        writeStorageValue(sessionStorage, key, sessionPatch[key]);
    }

    if (localStorage.getItem("keepSignedIn")) {
        for (const key of SESSION_REFRESH_KEYS) {
            writeStorageValue(localStorage, key, sessionPatch[key]);
        }
    }

    if (data.multi_branch !== undefined) {
        syncMultiBranchFlag(data.multi_branch);
    }

    if (data.org_admin_branch_selection !== undefined) {
        const merged = {
            ...JSON.parse(sessionStorage.getItem("customConfig") || "{}"),
            org_admin_branch_selection: data.org_admin_branch_selection !== false,
        };
        sessionStorage.setItem("customConfig", JSON.stringify(merged));
        if (localStorage.getItem("keepSignedIn")) {
            localStorage.setItem("customConfig", JSON.stringify(merged));
        }
        enforceOrgAdminBranchPolicy();
    }

    const nextGroupId = sessionPatch.user_group_id != null ? String(sessionPatch.user_group_id) : null;
    const permissionsChanged =
        JSON.stringify(permissions) !== previousPermissions;
    const groupChanged =
        previousGroupId != null &&
        nextGroupId != null &&
        String(previousGroupId) !== nextGroupId;

    if (permissionsChanged || groupChanged) {
        window.dispatchEvent(new Event(SESSION_STORAGE_HYDRATED_EVENT));
    }

    return { permissionsChanged, groupChanged };
}

/** Fetch latest permissions/roles from the server and update the browser session. */
export async function refreshAuthSession() {
    if (!sessionStorage.getItem("token")) {
        return { ok: false, reason: "no-token" };
    }

    try {
        const response = await apiClient.get(SESSION_REFRESH_URL);
        if (response?.status !== "success" || !response?.data) {
            return { ok: false, reason: "bad-response" };
        }

        const changes = applySessionPayload(response.data);
        return { ok: true, ...changes };
    } catch (error) {
        return { ok: false, reason: "request-failed", error };
    }
}
